import { FC, SyntheticEvent, useCallback, useState, useMemo } from 'react';

import { Typography } from '@mui/material';

import { useAccessibleAccounts } from 'shared/providers/AccessibleAccounts';

import { getInitials } from './UserSelector.utils';
import { StyledUserSelector } from './UserSelector.styled';
import { StyledAvatar } from './Avatar';
import { Menu } from './Menu';
import { GRID_AREA_AVATAR, GRID_AREA_NAME, GRID_AREA_ACCOUNT } from './UserSelector.constants';
import { Switcher } from './Switcher';

export const UserSelector: FC = () => {
  const { currentAccount, shouldShowAccountSwitcher } = useAccessibleAccounts();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLDivElement | null>(null);

  const fullName = currentAccount
    ? `${currentAccount.accountHolderForename} ${currentAccount.accountHolderSurname}`
    : '';

  const nameInitials = useMemo(() => getInitials(fullName), [fullName]);

  const handleClick = useCallback(
    (event: SyntheticEvent<HTMLDivElement, Event>) => {
      if (!shouldShowAccountSwitcher) return;

      setMenuAnchorEl(event.currentTarget);
    },
    [shouldShowAccountSwitcher]
  );

  const handleClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  return (
    <>
      <StyledUserSelector
        onClick={handleClick}
        shouldShowAccountSwitcher={shouldShowAccountSwitcher}
      >
        <StyledAvatar src={undefined} alt={fullName} sx={{ gridArea: GRID_AREA_AVATAR }}>
          {nameInitials}
        </StyledAvatar>
        <Typography gridArea={GRID_AREA_NAME} noWrap maxWidth='100%'>
          {fullName}
        </Typography>
        <Typography
          gridArea={GRID_AREA_ACCOUNT}
          noWrap
          variant='body2'
          color='text.secondary'
          maxWidth='100%'
        >
          {currentAccount?.accountBillingCode || currentAccount?.accountHolderEmail}
        </Typography>
        <Switcher
          menuAnchorEl={menuAnchorEl}
          shouldShowAccountSwitcher={shouldShowAccountSwitcher}
        />
      </StyledUserSelector>
      <Menu
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        onClose={handleClose}
        onClick={handleClose}
      />
    </>
  );
};
